import AuthenticatedClient from '@/repositories/clients/AuthenticatedClient';

const Client = AuthenticatedClient;
const resource = '/notifications';

export default {
  /**
   * List notifications, cursor-paginated.
   * @param {Object} params
   * @param {('all'|'unread'|'read')} [params.status]
   * @param {string[]} [params.severity]
   * @param {string} [params.notification_type]
   * @param {string} [params.since]      ISO datetime
   * @param {string} [params.until]      ISO datetime
   * @param {string} [params.cursor]
   * @param {number} [params.limit]
   */
  list(params = {}) {
    return Client.get(resource, { params });
  },

  /**
   * Recent notifications for the bell dropdown, served from the Redis ledger
   * (instant, ES-consistency-proof) with an ES fallback. Returns both the
   * recent `items` and the authoritative `unread_count` in one payload.
   * @param {number} [limit]
   */
  recent(limit = 5) {
    return Client.get(`${resource}/recent`, { params: { limit } });
  },

  get(id) {
    return Client.get(`${resource}/${encodeURIComponent(id)}`);
  },

  markRead(id) {
    return Client.post(`${resource}/${encodeURIComponent(id)}/read`);
  },

  markReadBulk(ids) {
    return Client.post(`${resource}/read`, { ids });
  },

  markAllRead() {
    return Client.post(`${resource}/read-all`);
  },

  delete(id) {
    return Client.delete(`${resource}/${encodeURIComponent(id)}`);
  },

  getPreferences() {
    return Client.get(`${resource}/preferences`);
  },

  updatePreferences(payload) {
    return Client.put(`${resource}/preferences`, payload);
  },

  /**
   * Start the Slack webhook verification flow: backend stores the code
   * in Redis and enqueues a worker task that POSTs the code to the
   * supplied Slack channel.
   */
  startSlackWebhookVerification(webhookUrl) {
    return Client.post(`${resource}/preferences/slack-webhook/start`, {
      webhook_url: webhookUrl,
    });
  },

  /** Confirm the code the user received in Slack. */
  confirmSlackWebhookVerification(code) {
    return Client.post(`${resource}/preferences/slack-webhook/confirm`, { code });
  },

  /** Remove the saved Slack webhook. */
  deleteSlackWebhook() {
    return Client.delete(`${resource}/preferences/slack-webhook`);
  },

  /**
   * Start the Telegram linking flow. Returns `{ deep_link, bot_username,
   * expires_in_seconds }`. The frontend opens `deep_link` in a new tab;
   * the user clicks Start in Telegram → backend webhook saves chat_id →
   * frontend polls `getTelegramStatus` to flip its UI.
   */
  startTelegramLink() {
    return Client.post(`${resource}/preferences/telegram/start`, {});
  },

  /** Has the user finished linking their Telegram chat? */
  getTelegramStatus() {
    return Client.get(`${resource}/preferences/telegram/status`);
  },

  /** Unlink Telegram (clears the chat_id). */
  deleteTelegramLink() {
    return Client.delete(`${resource}/preferences/telegram`);
  },

  /**
   * Start the outbound-webhook verification flow. Backend POSTs a one-shot
   * verification payload to the supplied URL. On 2xx the challenge is
   * stored in Redis; the user pastes the code their endpoint received into
   * `confirmWebhookVerification`.
   */
  startWebhookVerification(url) {
    return Client.post(`${resource}/preferences/webhook/start`, { url });
  },

  /**
   * Confirm the verification code observed at the user's endpoint. On success
   * the response contains the per-user HMAC `signing_secret` — shown to the
   * user EXACTLY ONCE so they can validate signatures in their consumer.
   * Subsequent reads only expose a 4-char hint.
   */
  confirmWebhookVerification(code) {
    return Client.post(`${resource}/preferences/webhook/confirm`, { code });
  },

  /** Remove the saved outbound webhook URL + signing secret. */
  deleteWebhook() {
    return Client.delete(`${resource}/preferences/webhook`);
  },

  /**
   * Web Push: fetch the platform's VAPID public key so the browser's
   * `pushManager.subscribe()` can request a subscription tied to us.
   */
  getWebPushVapidKey() {
    return Client.get(`${resource}/preferences/web-push/vapid-public-key`);
  },

  /** Register a Web Push subscription for the current device. */
  subscribeWebPush({ endpoint, p256dh, auth, userAgent }) {
    // Backend contract uses snake_case (`user_agent`); convert at the boundary.
    return Client.post(`${resource}/preferences/web-push/subscribe`, {
      endpoint,
      p256dh,
      auth,
      user_agent: userAgent,
    });
  },

  /** Unlink one device by its endpoint hash (the only id the UI ever sees). */
  unsubscribeWebPush(endpointHash) {
    return Client.post(`${resource}/preferences/web-push/unsubscribe`, {
      endpoint_hash: endpointHash,
    });
  },

  /** Display-only list of linked devices (endpoint hashes + UA + created_at). */
  listWebPushDevices() {
    return Client.get(`${resource}/preferences/web-push/subscriptions`);
  },

  /**
   * Fire a `platform.test.v1` notification to the authenticated user on
   * the channels they picked. The backend enqueues a Celery task; the
   * actual delivery happens on the worker. Frontend rate-limits to one
   * call per minute via the prefs page.
   */
  sendTestNotification({ channels, note = '' }) {
    return Client.post(`${resource}/preferences/test`, { channels, note });
  },
};
